import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  QuestionData,
  QuestionOption,
  AnswerKeySettings,
  IdealAnswerItem,
  SubQuestionErrors,
} from '../types'
import { isHtmlContentEmpty } from '@/utils/markdownConverter'

export const useQuestionValidation = () => {
  const { t } = useTranslation()
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const isOptionCheckValid = (options: QuestionOption[]): boolean => {
    return options.some(option => option.is_correct)
  }

  const isOptionTextValid = (options: QuestionOption[]): boolean => {
    return options.every(option => option.text && option.text.trim())
  }

  const isEssayTableValid = (
    answerKeySettings: AnswerKeySettings | undefined,
    idealAnswerItems: IdealAnswerItem[],
  ): boolean => {
    if (!answerKeySettings) return false

    const headers = answerKeySettings.headers
      ? answerKeySettings.headers.slice(1)
      : []
    const columnHeaders = answerKeySettings.column_headers || []

    const headersValidity =
      (headers.length > 0 ? headers.every(h => h.trim().length > 0) : false) &&
      (columnHeaders.length > 0
        ? columnHeaders.every(ch => ch.trim().length > 0)
        : false)

    const itemsValidity = idealAnswerItems.every(
      item => item.text.trim().length > 0,
    )

    return headersValidity && itemsValidity
  }

  const validateSubQuestions = (
    subQuestionsForSave: Record<string, QuestionData>,
  ): { isValid: boolean; errors: SubQuestionErrors } => {
    const errors: SubQuestionErrors = {}
    let isValid = true

    Object.keys(subQuestionsForSave).forEach(id => {
      const subQ = subQuestionsForSave[id]

      if (!subQ.text || subQ.text.trim() === '') {
        isValid = false
        errors[id] = t('Error! Question cannot be empty')
      } else if (subQ.score < 0) {
        isValid = false
        errors[id] = t(
          'Error! Please enter a valid score (any number more than or equal to 0)',
        )
      } else if (
        (subQ.type === 'checkbox' || subQ.type === 'radio') &&
        subQ.options &&
        !isOptionTextValid(subQ.options)
      ) {
        isValid = false
        errors[id] = t('Error! Option cannot be empty')
      } else if (
        (subQ.type === 'checkbox' || subQ.type === 'radio') &&
        subQ.score > 0 &&
        subQ.options &&
        !isOptionCheckValid(subQ.options)
      ) {
        isValid = false
        errors[id] = t('Error! Please mark at least one option as correct')
      } else if (
        subQ.type === 'essay' &&
        subQ.position_check === 'rubric_breakdown' &&
        !isEssayTableValid(
          subQ.answer_key_settings,
          subQ.ideal_answer_items || [],
        )
      ) {
        isValid = false
        errors[id] = t('Error! Inputs cannot be empty')
      } else if (
        subQ.type === 'essay' &&
        subQ.position_check === 'rubric_description' &&
        !isEssayTableValid(
          subQ.answer_key_settings,
          subQ.ideal_answer_items || [],
        )
      ) {
        isValid = false
        errors[id] = t('Error! Inputs cannot be empty')
      }
    })

    return { isValid, errors }
  }

  const validateQuestion = (
    questionData: Partial<QuestionData>,
    questionType: string,
    questionScore: number,
    idealToggle: boolean,
    idealText: string,
    subQuestionsForSave?: Record<string, QuestionData>,
    rubricPositionCheck?: string,
  ): boolean => {
    setShowError(false)
    setErrorMessage('')

    // Force get latest content from all SunEditor instances
    let freshIdealText = idealText
    let hasMathFieldContent = false

    try {
      const sunEditors = document.querySelectorAll(
        '.sun-editor-editable, .se-wrapper-wysiwyg',
      )

      sunEditors.forEach((editorEl: any) => {
        const mathFields = editorEl.querySelectorAll('math-field')

        mathFields.forEach((mf: any) => {
          const value = mf.value || ''
          if (value.trim() !== '' && !value.includes('\\placeholder')) {
            hasMathFieldContent = true
          }

          if (mf && typeof mf.blur === 'function') {
            mf.blur()
          }
        })

        const editorContent = editorEl.innerHTML || editorEl.textContent
        if (editorContent && editorContent.length > freshIdealText.length) {
          freshIdealText = editorContent
        }
      })
    } catch (error) {
      console.warn('Failed to check editors:', error)
    }

    const isQuestionEmpty = isHtmlContentEmpty(questionData.text || '')
    const isIdealAnswerEmpty = isHtmlContentEmpty(freshIdealText)
    const isActuallyIdealEmpty = isIdealAnswerEmpty && !hasMathFieldContent

    // Validation checks
    if (isQuestionEmpty) {
      setShowError(true)
      setErrorMessage(t('Error! Question cannot be empty'))
      return true
    }

    if (questionScore < 0) {
      setShowError(true)
      setErrorMessage(
        t(
          'Error! Please enter a valid score (any number more than or equal to 0)',
        ),
      )
      return true
    }

    if (
      (questionType === 'checkbox' || questionType === 'radio') &&
      questionData.options &&
      !isOptionTextValid(questionData.options)
    ) {
      setShowError(true)
      setErrorMessage(t('Error! Option cannot be empty'))
      return true
    }

    if (
      (questionType === 'checkbox' || questionType === 'radio') &&
      questionScore > 0 &&
      questionData.options &&
      !isOptionCheckValid(questionData.options)
    ) {
      setShowError(true)
      setErrorMessage(t('Error! Please mark at least one option as correct'))
      return true
    }

    if (questionType === 'matching' && questionData.matches) {
      const hasInvalidKey = questionData.matches.some(
        match => !match.term.length || !match.definition.length,
      )
      if (hasInvalidKey) {
        setShowError(true)
        setErrorMessage(t('Error! Answer should not be empty.'))
        return true
      }
    }

    if (
      questionType === 'group' &&
      (!subQuestionsForSave || Object.keys(subQuestionsForSave).length === 0)
    ) {
      setShowError(true)
      setErrorMessage(t('Error! Please add at least one sub-question'))
      return true
    }

    if (
      questionType === 'group' &&
      subQuestionsForSave &&
      Object.keys(subQuestionsForSave).length > 0
    ) {
      const { isValid } = validateSubQuestions(subQuestionsForSave)
      if (!isValid) {
        setShowError(true)
        setErrorMessage(t('Error! Please check the sub-questions'))
        return true
      }
    }

    if (
      questionType === 'essay' &&
      rubricPositionCheck === 'rubric_breakdown' &&
      !isEssayTableValid(
        questionData.answer_key_settings,
        questionData.ideal_answer_items || [],
      )
    ) {
      setShowError(true)
      setErrorMessage(t('Error! Inputs cannot be empty'))
      return true
    }

    if (
      questionType === 'essay' &&
      rubricPositionCheck === 'rubric_description' &&
      !isEssayTableValid(
        questionData.answer_key_settings,
        questionData.ideal_answer_items || [],
      )
    ) {
      setShowError(true)
      setErrorMessage(t('Error! Inputs cannot be empty'))
      return true
    }

    if (idealToggle && isActuallyIdealEmpty) {
      setShowError(true)
      setErrorMessage(t('Error! Ideal awnser cannot be empty'))
      return true
    }

    return false
  }

  return {
    showError,
    errorMessage,
    setShowError,
    setErrorMessage,
    validateQuestion,
    validateSubQuestions,
    isOptionCheckValid,
    isOptionTextValid,
    isEssayTableValid,
  }
}
